import { InputWithIcon } from '@/components/ui/vehicle/input-with-icon';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleFromIndexerStore } from '@/models/stores/vehicle-listings';
import type { VehicleFromIndexer } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { BatteryFull } from 'lucide-react';

export type BatteryCapacityInputProps = {
  id: string;
  value: VehicleFromIndexer['battery_capacity'];
  className?: string;
};

export function BatteryCapacityInput({
  id,
  value,
  className,
}: BatteryCapacityInputProps) {
  const updateBatteryCapacityMutation = useMutation({
    onMutate: async ({ batteryCapacity }) => {
      vehicleFromIndexerStore.updateBatteryCapacity({ id, batteryCapacity });
    },
    mutationFn: async ({
      batteryCapacity,
    }: {
      batteryCapacity: VehicleFromIndexer['battery_capacity'];
    }) => {
      return await pdkAxios.post(`/inventory/vehicles/${id}/battery_capacity`, {
        battery_capacity: batteryCapacity,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating battery capacity',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleBatteryCapacityChange = (newBatteryCapacity: string) => {
    // Handle empty string or invalid input
    if (newBatteryCapacity === '' || isNaN(parseInt(newBatteryCapacity))) {
      updateBatteryCapacityMutation.mutate({ batteryCapacity: 0 });
    } else {
      updateBatteryCapacityMutation.mutate({
        batteryCapacity: parseInt(newBatteryCapacity),
      });
    }
  };

  return (
    <div>
      <InputWithIcon
        icon={<BatteryFull className="h-4 w-4" />}
        value={value?.toString() || ''}
        placeholder="Battery capacity"
        onChange={handleBatteryCapacityChange}
        className={cn('pl-0', className)}
        inputClassName="w-7"
        suffix="kwh"
      />
    </div>
  );
}
